import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { EmptyState } from "../../components/ui/empty-state";
import { getPlanes, updatePlan, deletePlan } from '../../services/planesService';
import { getFamilias } from "../../services/familiasService";
import { Card, CardContent, CardFooter } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Switch, RadioPills } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { PlusCircle, Pencil, Trash2, Info, Shield, XCircle, Archive, Search, Plus } from "lucide-react";

export default function PlanesList() {
  const [planes, setPlanes] = useState([]);
  const [filteredPlanes, setFilteredPlanes] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [filter, setFilter] = useState("todos"); // New state for filter

  useEffect(() => {
    loadData();
  }, []);

  // Apply filter whenever planes or filter changes
  useEffect(() => {
    filterPlanes();
  }, [planes, filter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [planesData, familiasData] = await Promise.all([
        getPlanes(),
        getFamilias()
      ]);
      setPlanes(planesData);
      setFamilias(familiasData);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  // New function to filter planes based on status
  const filterPlanes = () => {
    if (filter === "todos") {
      setFilteredPlanes(planes);
    } else if (filter === "activos") {
      setFilteredPlanes(planes.filter(p => p.status === 'active' && p.archived !== 1 && p.archived !== true));
    } else if (filter === "inactivos") {
      setFilteredPlanes(planes.filter(p => p.status === 'inactive' && p.archived !== 1 && p.archived !== true));
    } else if (filter === "archivados") {
      setFilteredPlanes(planes.filter(p => p.archived === 1 || p.archived === true));
    }
  };

  // Update the handleArchiveToggle function
  const handleArchiveToggle = async (plan, archived) => {
    try {
      await updatePlan(plan.recordId, {
        archived: archived
      });
      
      // Update local state
      setPlanes(planes.map(p => 
        p.recordId === plan.recordId 
          ? { ...p, archived: archived } 
          : p
      ));
    } catch (error) {
      console.error("Error updating plan archive status:", error);
    }
  };

  // Group planes by familia
  const groupPlanesByFamilia = () => {
    const grouped = {};
    
    filteredPlanes.forEach(plan => {
      const familiaId = plan.fkFam || 'sin-familia';
      if (!grouped[familiaId]) {
        const familia = familias.find(f => f.recordId === familiaId) || { titulo: 'Sin Familia' };
        grouped[familiaId] = {
          familiaId,
          familiaNombre: familia.titulo,
          planes: []
        };
      }
      grouped[familiaId].planes.push(plan);
    });
    
    // Sort by familia name
    return Object.values(grouped).sort((a, b) => a.familiaNombre.localeCompare(b.familiaNombre));
  };

  const handleDeleteClick = (plan) => {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!planToDelete) return;
    
    try {
      await deletePlan(planToDelete.recordId);
      setPlanes(planes.filter(plan => plan.recordId !== planToDelete.recordId));
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error('Error deleting plan:', err);
      // Handle error
    }
  };

  const handleStatusToggle = async (plan, newStatus) => {
    try {
      // Only send the status field in the payload
      await updatePlan(plan.recordId, {
        status: newStatus ? 'active' : 'inactive'
      });
      
      // Update local state
      setPlanes(planes.map(p => 
        p.recordId === plan.recordId 
          ? { ...p, status: newStatus ? 'active' : 'inactive' } 
          : p
      ));
    } catch (error) {
      console.error("Error updating plan status:", error);
    }
  };

  // Helper function to group portal data by type
  const getPortalDataByType = (portalData, type) => {
    if (!portalData || !portalData.plan) return [];
    return portalData.plan.filter(item => item["plan::tipo"] === type);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Planes</h1>
          <p className="text-gray-600">Visualización de todos los planes disponibles</p>
        </div>
        <Link to="/admin/planes/new">
          <Button className="flex items-center gap-2 bg-[#007D6E] hover:bg-[#006358] text-white">
            <PlusCircle className="h-5 w-5" />
            Nuevo Plan
          </Button>
        </Link>
      </div>

      {/* Un valor entre varios, todos a la vista: es el radio pill del Yool DS
          (`RadioPills`, variante sin punto). Antes eran cuatro `Button` con el
          activo en teal — el mismo teal del CTA "Nuevo", que por la regla del
          sistema es el único de la vista. */}
      <RadioPills
        aria-label="Filtrar planes por estado"
        value={filter}
        onChange={setFilter}
        options={[
          { value: "todos", label: "Todos" },
          { value: "activos", label: "Activos" },
          { value: "inactivos", label: "Inactivos" },
          { value: "archivados", label: "Archivados" },
        ]}
      />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => <div key={i} className="rounded-xl border bg-white dark:bg-slate-900 p-5 space-y-3 animate-pulse"><div className="h-5 bg-slate-100 dark:bg-slate-800 rounded w-3/4"/><div className="h-4 bg-slate-100 dark:bg-slate-800 rounded w-1/2"/><div className="h-4 bg-slate-100 dark:bg-slate-800 rounded w-full"/></div>)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {planes.length === 0 ? (
            <div className="col-span-full">
              <EmptyState title="Sin planes" description="No hay planes configurados aún en el sistema." />
            </div>
          ) : (
            // Display plans with archive badge if needed
            filteredPlanes.map((plan) => (
              <Card 
                key={plan.uuid || plan.recordId} 
                className={`overflow-hidden ${plan.archived === 1 || plan.archived === true ? 'opacity-70' : ''}`}
              >
                <div className="relative">
                  {(plan.archived === 1 || plan.archived === true) && (
                    <div className="absolute top-2 left-2 z-10 bg-gray-700 text-white text-xs px-2 py-1 rounded-full">
                      Archivado
                    </div>
                  )}
                  <img 
                    src={plan.urlImagen || "https://yooldigital.com/assets/default.png"} 
                    alt={plan.nombre} 
                    className="w-full h-48 object-cover object-top"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://yooldigital.com/assets/default.png";
                    }}
                  />
                  {/* Status toggle - only show if not archived */}
                  {!(plan.archived === 1 || plan.archived === true) && (
                    <div className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md">
                      <div className="flex items-center space-x-2 px-2">
                        <Switch 
                          checked={plan.status === 'active'} 
                          onCheckedChange={(checked) => handleStatusToggle(plan, checked)}
                        />
                        <span className={`text-sm font-medium ${
                          plan.status === 'active' ? 'text-[var(--yool-success)]' : 'text-[var(--yool-error)]'
                        }`}>
                          {plan.status === 'active' ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Rest of your card content */}
                <CardContent className="p-4">
                  <h3 className="text-lg font-semibold">{plan.nombre}</h3>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">{plan.descripcion}</p>
                </CardContent>
                
             
                <CardFooter className="p-4 pt-0 flex justify-end gap-2">
                  <Link to={`/admin/planes/${plan.recordId}`}>
                    <Button variant="outline" size="sm" className="h-9 px-3">
                      <Pencil className="h-4 w-4 mr-1" />
                      Editar
                    </Button>
                  </Link>
                  {!(plan.archived === 1 || plan.archived === true) && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-9 px-3 text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                      onClick={() => handleArchiveToggle(plan, true)}
                    >
                      <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 8V18C5 19.1046 5.89543 20 7 20H17C18.1046 20 19 19.1046 19 18V8M5 8H19M5 8H3M19 8H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 12L14 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Archivar
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))
          )}
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el plan 
              "{planToDelete?.nombre}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

